import React, {useRef, useState} from 'react'
import {router, usePage} from "@inertiajs/react";
import {ButtonGroup, Col, Form, ListGroup, Modal, OverlayTrigger, Row, Tooltip} from 'react-bootstrap';
import {Icon} from '@mdi/react'
import {
    mdiCardAccountMailOutline,
    mdiClipboardEditOutline,
    mdiCodeJson,
    mdiEmailArrowLeftOutline,
    mdiEye,
    mdiFileDocumentPlusOutline,
    mdiFileDelimitedOutline,
    mdiFilePdfBox,
    mdiInvoiceTextSendOutline,
    mdiRefresh, mdiTrashCanOutline
} from '@mdi/js'
import {useEnhancedTranslation} from "laravel-inertia-backend";
import {allExpanded, darkStyles, JsonView} from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import AddressComponent from "../../Components/AddressComponent.jsx";
import ShippingInfo from "../../Components/ShippingInfo.jsx";
import {Button, FlexToast, useExternalControls, Can} from "laravel-inertia-backend";
import WeclappCustomerSearchModal from "./WeclappCustomerSearchModal.jsx";
import AddressComparisonModal from "./AddressComparisonModal.jsx";
import HoverLabelButton from "../../Components/HoverLabelButton.jsx";
import SpinnerModal from "../../Components/SpinnerModal.jsx";
import axios from "axios";
import useTransmissionPolling from "../../hooks/useTransmissionPolling.js";

// Belegnummer mit festem Präfix zeigen: Weclapp liefert Aufträge als "AB2940",
// Angebote je nach Nummernkreis mit oder ohne "AN" - angezeigt wird immer "AB 2940" bzw. "AN 1002".
function formatBelegnummer(prefix, nummer) {
    const rest = String(nummer).replace(new RegExp(`^${prefix}\\s*`, 'i'), '');
    return `${prefix} ${rest}`;
}

export default function ChildView({order}) {
    if (!order) {
        return null;
    }

    const {t, getLocales} = useEnhancedTranslation();
    const [values, setValues] = useState({
        first_name: order.customer_name,
        last_name: order.last_name,
        email: order.email,
    });
    const [modalVisible, setModalVisible] = useState(false);
    const [dumpModalVisible, setDumpModalVisible] = useState(false);
    const [previewResponse, setPreviewResponse] = useState(null);
    // Zustand für die HTML-Antwort
    const [htmlResponse, setHtmlResponse] = useState('');
    const shippingInfo = order?.shippinginfo ? JSON.parse(order.shippinginfo) : null;
    const [weclappCustomerSearchModal, setWeclappCustomerSearchModal] = useState(false);
    const [addressComparisonModal, setAddressComparisonModal] = useState(false);
    const [deliveryWeclappAddress, setDeliveryWeclappAddress] = useState(null);
    const [deliveryOrderAddress, setDeliveryOrderAddress] = useState(null);
    const [invoiceWeclappAddress, setInvoiceWeclappAddress] = useState(null);
    const [invoiceOrderAddress, setInvoiceOrderAddress] = useState(null);
    const [showDeliveryDecision, setShowDeliveryDecision] = useState(false);
    const [showInvoiceDecision, setShowInvoiceDecision] = useState(false);
    const [inProgress, setInProgress] = useState(false);
    const [failedJobId, setFailedJobId] = useState(null);
    const [progressMessage, setProgressMessage] = useState(t('Transmitting order to weclapp...'));
    const {addToast} = useExternalControls();
    const {poll} = useTransmissionPolling();
    const {weclappBaseUrl, user_roles, can_order_editor, can_weclapp_quotation} = usePage().props;
    // Belegart der laufenden bzw. zuletzt gestarteten Übertragung ("salesOrder" | "quotation").
    const documentType = useRef('salesOrder');
    const pendingDocumentType = useRef('salesOrder');
    const isQuotationRun = () => documentType.current === 'quotation';

    function handleChange(e) {
        const key = e.target.id;
        const value = e.target.value;
        setValues(values => ({
            ...values,
            [key]: value,
        }))
    }

    function handlePreview() {
        axios.get(`order/preview`, {params: {id: order.id}})
            .then(response => {
                setHtmlResponse(response.data);
                setModalVisible(true);
            });
    }

    function handlePdf() {
        window.open(`order/download/` + order.id, '_blank');
    }

    function handleCsv() {
        window.open(`order/csv/` + order.id, '_blank');
    }

    function handleDumpShow() {
        if (order.request_dump) {
            setDumpModalVisible(true);
        }
    }

    function handleRecreate() {
        router.get(`order/recreate/${order.id}`);
    }

    function handleResendEmail() {
        if (window.confirm(t("Are you sure you want to resend order email?"))) {
            router.get(`order/resend/${order.id}`);
        }
    }

    function handleTransferEmail() {
        router.get(`order/transfer/${order.id}`);
    }

    function updateOrderState(state) {
        router.reload({only: ['extradata', 'orders'], data: {state: state}});
    }

    function getConnectionType(id) {
        switch (id) {
            case 1:
                return 'B2B';
            case 2:
                return 'B2C alt';
            case 3:
                return 'B2C';
            default:
                return '';
        }
    }

    function buildMailToLink() {
        const shop_order_id = order.shop_order_id;
        const connection_type = getConnectionType(order.connection_id);

        const subject = `RE: Molotow ${connection_type} Bestellung ${shop_order_id}`;
        return `mailto:${order.customer_email}?subject=${subject}`;
    }

    const handleWeclappCustomerSearch = () => {
        setWeclappCustomerSearchModal(true);
    }

    const handleWeclappCreateOrder = () => startWeclappTransmission('salesOrder');
    const handleWeclappCreateQuotation = () => startWeclappTransmission('quotation');

    // Auftrag und Angebot teilen sich die Adressprüfung und den Übertragungsweg.
    const startWeclappTransmission = async (type) => {
        pendingDocumentType.current = type;
        documentType.current = type;
        setInProgress(true);
        setProgressMessage(t('Checking delivery address...'));

        try {
            // Precheck delivery & invoice addresses
            const precheckResponse = await axios.get(`/weclapp/order/precheck/${order.id}`);

            if (precheckResponse.data.error) {
                throw new Error(precheckResponse.data.message || 'Error checking addresses');
            }

            const delivery = precheckResponse.data.delivery;
            const invoice = precheckResponse.data.invoice;

            const deliveryDiff = delivery?.isDifferent === true;
            const invoiceDiff = invoice?.isDifferent === true;

            if (deliveryDiff || invoiceDiff) {
                setInProgress(false);
                if (deliveryDiff) {
                    setDeliveryWeclappAddress(delivery.weclappAddress || null);
                    setDeliveryOrderAddress(delivery.orderAddress || null);
                    setShowDeliveryDecision(true);
                } else {
                    setShowDeliveryDecision(false);
                }

                if (invoiceDiff) {
                    setInvoiceWeclappAddress(invoice.weclappAddress || null);
                    setInvoiceOrderAddress(invoice.orderAddress || null);
                    setShowInvoiceDecision(true);
                } else {
                    setShowInvoiceDecision(false);
                }

                setAddressComparisonModal(true);
                return;
            }

            // No differences, proceed with order creation with both flags false
            await processOrderCreation(false, false, type);
        } catch (err) {
            setInProgress(false);

            // Handle error
            let errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message;

            addToast(
                <FlexToast
                    icon={'images/logo_weclapp.svg'}
                    variant={'danger'}
                    title={"Weclapp"}
                    autohide={false}
                    message={t('order :order_id submit failed: :error', {
                        'order_id': order.id,
                        'error': errorMessage
                    })}
                />
            );
        } finally {
            setInProgress(false);
        }
    }

    // Pollt den Job bis Abschluss und meldet Erfolg; wirft bei Fehler weiter.
    const trackTransmission = async (jobId) => {
        await poll(jobId, {
            onProgress: (s) => setProgressMessage(t('Processing :percent % done', {
                percent: s.progress_percent ?? '?'
            })),
        });
        addToast(
            <FlexToast
                icon={'images/logo_weclapp.svg'}
                title={"Weclapp"}
                autohide={true}
                message={isQuotationRun()
                    ? t('order :order_id submitted as quotation successfully', {'order_id': order.id})
                    : t('order :order_id submitted successfully', {'order_id': order.id})}
            />
        );
        router.reload({only: ['extradata']});
    }

    const showTransmissionError = (err) => {
        let errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message;
        addToast(
            <FlexToast
                icon={'images/logo_weclapp.svg'}
                variant={'danger'}
                title={"Weclapp"}
                autohide={false}
                message={t('order :order_id submit failed: :error', {
                    'order_id': order.id,
                    'error': errorMessage
                })}
            />
        );
    }

    const processOrderCreation = async (useWeclappDeliveryAddress, useWeclappInvoiceAddress, type = pendingDocumentType.current) => {
        documentType.current = type;
        setInProgress(true);
        setProgressMessage(type === 'quotation'
            ? t('Creating quotation in weclapp...')
            : t('Transmitting order to weclapp...'));
        setFailedJobId(null);

        let startedJobId = null;
        try {
            // Übertragung als Worker-Job starten; der Job verarbeitet alle Chunks im Hintergrund.
            const endpoint = type === 'quotation' ? '/weclapp/quotation/create' : '/weclapp/order/create';
            const {data} = await axios.get(endpoint, {
                params: {
                    order: order.id,
                    use_weclapp_delivery_address: useWeclappDeliveryAddress,
                    use_weclapp_invoice_address: useWeclappInvoiceAddress,
                }
            });
            startedJobId = data.jobId;

            await trackTransmission(startedJobId);
        } catch (err) {
            if (startedJobId) {
                setFailedJobId(startedJobId); // ermöglicht Wiederaufnahme
            }
            showTransmissionError(err);
        } finally {
            setInProgress(false);
        }
    }

    // Setzt eine fehlgeschlagene Übertragung fort (Wiederaufnahme ab processed_chunks).
    const resumeTransmission = async () => {
        if (!failedJobId) return;
        setInProgress(true);
        setProgressMessage(t('Transmitting order to weclapp...'));
        try {
            const {data} = await axios.post(`/weclapp/transmission/${failedJobId}/retry`);
            await trackTransmission(data.jobId);
            setFailedJobId(null);
        } catch (err) {
            showTransmissionError(err);
        } finally {
            setInProgress(false);
        }
    }

    const openWeclappCustomer = (id) => {
        window.open(`${weclappBaseUrl}webapp/view/party/PartyDetail.page?entityId=${id}`, '_blank');
    }
    const openWeclappOrder = (id) => {
        window.open(`${weclappBaseUrl}webapp/view/op/order/salesOrderDetail.page?entityId=${id}`, '_blank');
    }
    const openWeclappQuotation = (id) => {
        window.open(`${weclappBaseUrl}app/quotation/${id}`, '_blank');
    }

    function unlinkWeclappOrder(orderId) {
        router.get(`weclapp/order/unlink/${orderId}`);
    }

    const handleCustomerSelect = (selectedCustomer,selectedContact = null) => {
        router.reload({only: ['extradata', 'orders'], data: {weclapp_customer: selectedCustomer.id,weclapp_record_party:selectedContact?.id}});
    }

    const hasWeclappOrder = !!order.weclapp_order_id;
    const hasWeclappQuotation = !!order.weclapp_quotation_id;
    const hasWeclappDocument = hasWeclappOrder || hasWeclappQuotation;
    const transferDisabled = !order.status || !order.weclapp_customer || hasWeclappDocument;

    // Deaktivierte Knöpfe bleiben für die Tooltips ansprechbar (aria-disabled statt disabled-Attribut),
    // damit der Hinweis, warum sie gerade nicht gehen, lesbar ist.
    const softDisabled = (disabled, handler) => ({
        className: disabled ? 'disabled' : undefined,
        'aria-disabled': disabled || undefined,
        style: disabled ? {pointerEvents: 'auto', cursor: 'not-allowed'} : undefined,
        onClick: disabled ? (e) => e.preventDefault() : handler,
    });

    function transferHint(kind) {
        if (hasWeclappOrder) return `Zu dieser Bestellung gibt es in Weclapp bereits einen Auftrag (${order.weclapp_order_number ? formatBelegnummer('AB', order.weclapp_order_number) : 'verknüpft'}). Ein Administrator kann die Verknüpfung lösen.`;
        if (hasWeclappQuotation) return `Zu dieser Bestellung gibt es in Weclapp bereits ein Angebot (${order.weclapp_quotation_number ? formatBelegnummer('AN', order.weclapp_quotation_number) : 'verknüpft'}). Ein Administrator kann die Verknüpfung lösen.`;
        if (!order.status) return `Die Bestellung hat Fehler und kann nicht ${kind === 'quotation' ? 'als Angebot' : 'als Auftrag'} übertragen werden.`;
        if (!order.weclapp_customer) return 'Zuerst den Weclapp-Kunden zuordnen oder anlegen (erster Knopf).';
        return null;
    }

    return (
        <div className={"order-details"}>
            <Row>
                <Col>
                    <h5>{t('order details')}</h5>
                    <div><strong>{t('order id')}:</strong> {order.shop_order_id}<br/>
                        <strong>{t('date')}:</strong> {order.order_date}<br/>
                        <strong>Kunde:</strong> {order.customer_name} <a
                            href={buildMailToLink()}>{order.customer_email}</a><br/>
                        <strong>Email-Status:</strong> <span
                            className={order.status ? '' : 'text-danger'}>{order.status ? t('success') : t('error')}</span><br/>
                        <strong>Abgeschlossen: </strong>
                        <Form.Check
                            inline
                            className={"cursor-pointer"}
                            type="checkbox"
                            id="manualStateCheckbox"
                            checked={order.manual_state === 1}
                            onChange={(e) => {
                                const updatedManualState = e.target.checked ? 1 : 0;
                                updateOrderState(updatedManualState);
                            }}
                        /><br/>
                        <strong>Gesendet:</strong> {order.send ? order.send : 'noch nicht gesendet'}</div>
                </Col>
                <Col>
                    <h5>{t('billing address')}</h5>
                    <AddressComponent address={order.billing_address}/>
                    {order.vat_id && <div>{t('vat.ID')}: {order.vat_id}</div>}
                </Col>
                <Col>
                    <h5>{t('shipping address')}</h5>
                    <AddressComponent address={order.shipping_address}/>
                </Col>
                <Col>
                    <h5>{t('shipping info')}
                    </h5>
                    <ShippingInfo shippingInfo={shippingInfo}/>
                </Col>
                <Can permission={'weclapp.view'}>
                    <Col>
                        <h5 style={{display: "flex", alignItems: "center"}}>
                            <img alt={"Weclapp Logo"} src={"images/logo_weclapp.svg"}
                                 style={{height: "15px", marginRight: "5px"}}/>
                            {t('Weclapp')}
                        </h5>
                        <table className={"weclapp-block"} style={{borderCollapse: "collapse"}}>
                            <tbody>
                            <tr>
                                <th scope={"row"} style={{paddingRight: "0.6rem", verticalAlign: "top"}}>Kunde:</th>
                                <td>
                                    {order.weclapp_customer ?
                                        <span onClick={() => openWeclappCustomer(order.weclapp_customer)}
                                              style={{cursor: "pointer"}}
                                              className={"text-success"}>{order.weclapp_customer_number || order.weclapp_customer}</span> :
                                        <span className={"text-danger"}>nicht verknüpft</span>}
                                </td>
                            </tr>
                            <tr>
                                <th scope={"row"} style={{paddingRight: "0.6rem", verticalAlign: "top"}}>Bestellung:</th>
                                <td>
                                    {hasWeclappOrder || hasWeclappQuotation ?
                                        <>
                                            <span
                                                onClick={() => hasWeclappOrder
                                                    ? openWeclappOrder(order.weclapp_order_id)
                                                    : openWeclappQuotation(order.weclapp_quotation_id)}
                                                style={{cursor: "pointer"}}
                                                className={"text-success"}>
                                                {hasWeclappOrder
                                                    ? (order.weclapp_order_number ? formatBelegnummer('AB', order.weclapp_order_number) : `AB (ID ${order.weclapp_order_id})`)
                                                    : (order.weclapp_quotation_number ? formatBelegnummer('AN', order.weclapp_quotation_number) : `AN (ID ${order.weclapp_quotation_id})`)}
                                            </span>
                                            {user_roles.indexOf('admin') !== -1 &&
                                                <OverlayTrigger placement={"top"}
                                                                overlay={<Tooltip>Verknüpfung mit dem Weclapp-Beleg lösen. Der Beleg selbst bleibt in Weclapp bestehen.</Tooltip>}>
                                                    <span className={"ms-1"} style={{cursor: "pointer"}}>
                                                        <Icon onClick={() => unlinkWeclappOrder(order.id)}
                                                              path={mdiTrashCanOutline} size={"1em"}/>
                                                    </span>
                                                </OverlayTrigger>}
                                        </> :
                                        <span className={"text-danger"}>nicht übertragen</span>}
                                </td>
                            </tr>
                            {hasWeclappOrder && order.weclapp_order_date &&
                                <tr>
                                    <th scope={"row"}></th>
                                    <td><span className={"text-success"}>{order.weclapp_order_date}</span></td>
                                </tr>}
                            </tbody>
                        </table>
                    </Col>
                </Can>
                <Col md={2} className={"text-end"}>
                    <Can permission={'weclapp.view'}>
                        <ButtonGroup vertical className={"me-2"}>
                            <HoverLabelButton label={"Kontakt verknüpfen"} {...softDisabled(hasWeclappDocument, handleWeclappCustomerSearch)}
                                    tooltip={hasWeclappDocument ? transferHint() : "Kontakt in Weclapp suchen oder neu anlegen und der Bestellung zuordnen"}
                                    variant={"weclapp"}>
                                <Icon path={mdiCardAccountMailOutline} size={1}/>
                            </HoverLabelButton>
                            {can_weclapp_quotation && (
                                <HoverLabelButton label={"Angebot erstellen"} {...softDisabled(transferDisabled, handleWeclappCreateQuotation)}
                                        tooltip={transferHint('quotation') ??
                                            "Angebot in Weclapp erstellen: legt ein ANGEBOT an, keinen Auftrag. " +
                                            "Übertragen werden Kunde, Adressen und alle Positionen mit Menge, genau wie bei der Auftragsübertragung. " +
                                            "Es wird keine E-Mail an den Kunden versendet."}
                                        variant={"weclapp"}>
                                    <Icon path={mdiFileDocumentPlusOutline} size={1}/>
                                </HoverLabelButton>
                            )}
                            <HoverLabelButton label={"Auftrag erstellen"} {...softDisabled(transferDisabled, handleWeclappCreateOrder)}
                                    tooltip={transferHint('salesOrder') ??
                                        "Bestellung als AUFTRAG an Weclapp übertragen: legt in Weclapp einen Auftrag an " +
                                        "(Kunde, Adressen, alle Positionen)." +
                                        (can_weclapp_quotation ? " Für ein unverbindliches Angebot gibt es den Knopf darüber." : "")}
                                    variant={"weclapp"}>
                                <Icon path={mdiInvoiceTextSendOutline} size={1}/>
                            </HoverLabelButton>
                            {can_order_editor && (
                                <HoverLabelButton label={"Editor öffnen"} {...softDisabled(!order.weclapp_order_id, () => router.visit(`/order-editor/${order.id}`))}
                                        tooltip={order.weclapp_order_id
                                            ? "Auftrags-Editor öffnen: den verknüpften weclapp-Auftrag laden, Bestand prüfen und bearbeiten"
                                            : "Auftrags-Editor: erst verfügbar, wenn die Bestellung an weclapp übertragen ist"}
                                        variant={"weclapp"}>
                                    <Icon path={mdiClipboardEditOutline} size={1}/>
                                </HoverLabelButton>
                            )}
                            {failedJobId && (
                                <HoverLabelButton label={"Fortsetzen"} disabled={inProgress}
                                        tooltip={"Übertragung fortsetzen"} variant={"warning"}
                                        onClick={resumeTransmission}>
                                    <Icon path={mdiInvoiceTextSendOutline} size={1}/>
                                </HoverLabelButton>
                            )}
                        </ButtonGroup>
                    </Can>
                    <ButtonGroup vertical>
                        {order.request_dump &&
                            <Button tooltip={"Rohdaten der Bestellung anzeigen"} tooltipPlacement={"left"}
                                    onClick={handleDumpShow}>
                                <Icon path={mdiCodeJson} size={1}/>
                            </Button>}
                        <Button disabled={order.status === 0} tooltip={"PDF der Bestellung anzeigen"}
                                tooltipPlacement={"left"} onClick={handlePdf}>
                            <Icon path={mdiFilePdfBox} size={1}/>
                        </Button>
                        <Button disabled={order.status === 0} tooltip={"CSV der Bestellung anzeigen"}
                                tooltipPlacement={"left"} onClick={handleCsv}>
                            <Icon path={mdiFileDelimitedOutline} size={1}/>
                        </Button>
                        <Button disabled={order.status === 0} tooltip={"Vorschau der Bestellung anzeigen"}
                                tooltipPlacement={"left"}
                                onClick={handlePreview}>
                            <Icon path={mdiEye} size={1}/>
                        </Button>
                        <Button disabled={order.assigned_user_ids === null}
                                tooltip={"E-Mail zur Beantwortung zustellen"} tooltipPlacement={"left"}
                                onClick={handleTransferEmail}>
                            <Icon path={mdiEmailArrowLeftOutline} size={1}/>
                        </Button>

                        <Button tooltip={"Bestellung neu erzeugen"} tooltipPlacement={"left"} onClick={handleRecreate}>
                            <Icon path={mdiRefresh} size={1}/>
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>
            {order.comment && <Row>
                <h5>Bestellkommentar</h5>
                <div className={"order-comment"}
                     dangerouslySetInnerHTML={{__html: order.comment}}
                     style={{
                         backgroundColor: "#fffdd9",
                         border: "2px solid #f00",
                         fontWeight: "bold"
                     }}></div>
            </Row>}
            {order.withdrawal_comment && <Row>
                <h5 style={{marginTop: "1rem"}}>Widerrufs-Kommentar</h5>
                <div className={"order-comment"}
                     style={{
                         backgroundColor: "#fffdd9",
                         border: "2px solid #f00",
                         fontWeight: "bold",
                         whiteSpace: "pre-wrap"
                     }}>{order.withdrawal_comment}</div>
            </Row>}
            <Row>
                {!order.status && (<>
                    <h5>{t('order errors')}</h5>
                    {order.order_errors && order.order_errors.length > 0 && (

                        <ListGroup>
                            {order.order_errors.map((error, index) => (
                                <ListGroup.Item variant="danger" key={index}>
                                    <strong>SKU:</strong> {error.sku}, <strong>Description:</strong> {error.description}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </>)}
            </Row>
            <SpinnerModal show={inProgress} message={progressMessage} color={"#1AE39C"}/>
            <Modal show={modalVisible} fullscreen={true} onHide={() => setModalVisible(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('ordermail preview')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div dangerouslySetInnerHTML={{__html: htmlResponse}}/>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setModalVisible(false)}>
                        {t('Close')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={dumpModalVisible} fullscreen={true} onHide={() => setDumpModalVisible(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('ordermail request dump')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <JsonView data={order.request_dump} shouldExpandNode={allExpanded} style={darkStyles}/>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDumpModalVisible(false)}>
                        {t('Close')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <WeclappCustomerSearchModal
                show={weclappCustomerSearchModal}
                onHide={() => setWeclappCustomerSearchModal(false)}
                onCustomerSelect={handleCustomerSelect}
                order={order}/>
            <AddressComparisonModal
                show={addressComparisonModal}
                onHide={() => setAddressComparisonModal(false)}
                deliveryWeclappAddress={deliveryWeclappAddress}
                deliveryOrderAddress={deliveryOrderAddress}
                invoiceWeclappAddress={invoiceWeclappAddress}
                invoiceOrderAddress={invoiceOrderAddress}
                showDelivery={showDeliveryDecision}
                showInvoice={showInvoiceDecision}
                onConfirm={(useWeclappDeliveryAddress, useWeclappInvoiceAddress) => {
                    setAddressComparisonModal(false);
                    processOrderCreation(useWeclappDeliveryAddress, useWeclappInvoiceAddress, pendingDocumentType.current);
                }}
            />
        </div>
    )
}
